import React, { useState, useEffect } from 'react';
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";


const Bmi = () => {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('kg');
  const [heightUnit, setHeightUnit] = useState('m');
  const [bmi, setBmi] = useState(0.0);
  const [category, setCategory] = useState('Not Calculated');

  const convertWeightToKg = (weight, unit) => {
    if (unit === 'kg') return weight;
    if (unit === 'lbs' || unit === 'pounds') return weight * 0.453592;
    if (unit === 'stone') return weight * 6.35029;
    if (unit === 'oz') return weight * 0.0283495;
    return weight;
  };

  const convertHeightToMeters = (height, unit) => {
    if (unit === 'm') return height;
    if (unit === 'cm') return height / 100;
    if (unit === 'in') return height * 0.0254;
    if (unit === 'ft') return height * 0.3048;
    if (unit === 'yard') return height * 0.9144;
    return height;
  };

  const calculateBMI = (weight, height, weightUnit, heightUnit) => {
    if (weight > 0 && height > 0) {
      const weightInKg = convertWeightToKg(weight, weightUnit);
      const heightInMeters = convertHeightToMeters(height, heightUnit);

      const bmiValue = weightInKg / (heightInMeters * heightInMeters);
      setBmi(bmiValue.toFixed(2));

      let bmiCategory = '';
      if (bmiValue < 18.5) {
        bmiCategory = 'Underweight';
      } else if (bmiValue >= 18.5 && bmiValue < 24.9) {
        bmiCategory = 'Normal weight';
      } else if (bmiValue >= 25 && bmiValue < 29.9) {
        bmiCategory = 'Overweight';
      } else {
        bmiCategory = 'Obese';
      }
      setCategory(bmiCategory);
    } else {
      setBmi(0.0);
      setCategory('Not Calculated');
    }
  };

  useEffect(() => {
    calculateBMI(weight, height, weightUnit, heightUnit);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [weight, height, weightUnit, heightUnit]);

  const handleWeightChange = (e) => {
    setWeight(parseFloat(e.target.value) || '');
  };

  const handleHeightChange = (e) => {
    setHeight(parseFloat(e.target.value) || '');
  };

  const handleWeightUnitChange = (e) => {
    setWeightUnit(e.target.value);
  };

  const handleHeightUnitChange = (e) => {
    setHeightUnit(e.target.value);
  };


  return (
    <Layout title="BMI (Body Mass Index) Calculator" path="body-mass-index">
          <section className="bmi-calculator-section section-padding" id="bmi-calculator">
        <div className="container">
          <div className="row">
            <div className="col-lg-9 col-md-8 mb-4">
              <div className="p-4 border rounded shadow" style={{ border: '1px solid black' }}>
                <h3 className="text-center mb-4">BMI (Body Mass Index) Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="weight" className="form-label">
                        Weight
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Weight:</strong> The body mass in the selected unit."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="weight"
                          step="0.1"
                          placeholder="Enter weight"
                          value={weight}
                          onChange={handleWeightChange}
                        />
                        <select
                          className="form-select ms-2"
                          id="weightUnit"
                          value={weightUnit}
                          onChange={handleWeightUnitChange}
                        >
                          <option value="kg">kg</option>
                          <option value="lbs">lbs</option>
                          <option value="pounds">Pounds</option>
                          <option value="stone">Stone</option>
                          <option value="oz">Ounces</option>
                        </select>
                      </div>
                      <small className="form-text text-muted">Choose unit (kg, lbs, pounds, stone, oz)</small>
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="height" className="form-label">
                        Height
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Height:</strong> The body height in the selected unit."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="height"
                          step="0.01"
                          placeholder="Enter height"
                          value={height}
                          onChange={handleHeightChange}
                        />
                        <select
                          className="form-select ms-2"
                          id="heightUnit"
                          value={heightUnit}
                          onChange={handleHeightUnitChange}
                        >
                          <option value="m">Meters</option>
                          <option value="cm">Centimeters</option>
                          <option value="in">Inches</option>
                          <option value="ft">Feet</option>
                          <option value="yard">Yards</option>
                        </select>
                      </div>
                      <small className="form-text text-muted">Choose unit (m, cm, in, ft, yard)</small>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        BMI: <span className="fw-bold ms-3">{bmi}</span>
                      </h6>
                      <h6>
                        Category: <span className="fw-bold ms-3">{category}</span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Advertise />
          </div>
        </div>
      </section>

    <section className="introduction-section" id="introduction">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="p-5">

              <h3 className="text-center mb-5">Introduction to the BMI Calculator</h3>

              <h4 className="mt-3 mb-3">What is a BMI Calculator?</h4>
              <p>A <strong>BMI Calculator</strong> is a tool used to assess body fat based on an individual's weight and height. It provides a simple metric to categorize health risk and determine if a person is underweight, normal weight, overweight, or obese.</p>

              <h4 className="mt-4 mb-3">Why Use a BMI Calculator?</h4>
              <p>Using a BMI calculator has several benefits:</p>
              <ul>
                <li><strong>Health Awareness:</strong> Provides insight into an individual’s weight category and potential health risks.</li>
                <li><strong>Quick Assessment:</strong> Simple and fast method to gauge fitness levels.</li>
                <li><strong>Preventive Health:</strong> Helps identify risk factors associated with weight-related health issues.</li>
                <li><strong>Baseline for Action:</strong> Supports informed decisions about lifestyle changes or seeking medical advice.</li>
              </ul>

              <h4 className="mt-4 mb-3">Common Misconceptions about BMI</h4>
              <p>Many people assume that BMI is a perfect measure of health, but it's essential to understand that it doesn't differentiate between muscle and fat. Therefore, athletes and muscular individuals may be classified as overweight or obese despite having low body fat.</p>

              <h4 className="mt-4 mb-3">Are There Better Alternatives?</h4>
              <p>While BMI is a helpful tool for a general overview, other metrics such as body fat percentage, waist-to-hip ratio, and more detailed health assessments can provide a more comprehensive view of an individual's health status.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
    </Layout>
  )
}

export default Bmi
